import api from "./api";
import { toast } from "sonner";

// Add the saree from a try-on look straight to the cart (one tap).
export async function shopLook(productId, addItem) {
  try {
    const { data } = await api.get(`/products/${productId}`);
    addItem(data);
    toast.success(`${data.name} added to cart`);
    return true;
  } catch (e) {
    toast.error("Could not add this saree to cart");
    return false;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Compose a branded, shareable card (dark navy + gold) from a try-on image.
export async function buildBrandedCard(imageUrl, productName = "") {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#0B0E1A";
  ctx.fillRect(0, 0, W, H);

  try { await document.fonts.ready; } catch (e) { console.warn("Fonts not ready for branded card:", e); }

  // Try-on image (cover) inside a framed region
  const x = 60, y = 60, w = W - 120, h = 1010;
  try {
    const img = await loadImage(imageUrl);
    const scale = Math.max(w / img.width, h / img.height);
    const sw = w / scale, sh = h / scale;
    const sx = (img.width - sw) / 2, sy = (img.height - sh) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
    ctx.restore();
  } catch (e) { console.warn("Could not draw try-on image onto card:", e); }

  // Gold frame
  ctx.strokeStyle = "#D4AF37";
  ctx.lineWidth = 3;
  ctx.strokeRect(x, y, w, h);

  // Footer text
  ctx.textAlign = "center";
  ctx.fillStyle = "#D4AF37";
  ctx.font = "600 26px 'JetBrains Mono', monospace";
  ctx.fillText("AI VIRTUAL DRAPING STUDIO", W / 2, 1140);

  ctx.fillStyle = "#F3E5AB";
  ctx.font = "700 84px 'Cormorant Garamond', serif";
  ctx.fillText("Tashora", W / 2, 1225);

  if (productName) {
    ctx.fillStyle = "#E5D3B3";
    ctx.font = "500 40px 'Cormorant Garamond', serif";
    ctx.fillText(productName, W / 2, 1285);
  }

  const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.92));
  const url = canvas.toDataURL("image/png");
  return { blob, url };
}

function triggerDownload(url) {
  const a = document.createElement("a");
  a.href = url;
  a.download = "tashora-look.png";
  a.click();
}

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export async function downloadCard(imageUrl, productName = "") {
  const { url } = await buildBrandedCard(imageUrl, productName);
  triggerDownload(url);
  toast.success("Branded card downloaded");
}

export async function shareWhatsApp(imageUrl, productName = "") {
  const { url } = await buildBrandedCard(imageUrl, productName);
  triggerDownload(url);
  const text = encodeURIComponent(`Check out my look in the ${productName || "saree"} from Tashora! ✨ ${window.location.origin}`);
  window.open(`https://wa.me/?text=${text}`, "_blank");
  toast.success("Card downloaded — attach it in WhatsApp to share");
}

export async function shareInstagram(imageUrl, productName = "") {
  const { blob, url } = await buildBrandedCard(imageUrl, productName);
  // On mobile, the native share sheet surfaces Instagram Stories directly.
  const file = new File([blob], "tashora-look.png", { type: "image/png" });
  if (isMobile() && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "My Tashora look", text: `My look in the ${productName || "saree"} — Tashora ✨` });
      return;
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
  }
  // Fallback: save the card and open Instagram to post it to a Story
  triggerDownload(url);
  toast.success("Card saved! Opening Instagram — add it to your Story ✨");
  window.location.href = "instagram://story-camera";
  setTimeout(() => window.open("https://www.instagram.com/", "_blank"), 800);
}

export async function shareNative(imageUrl, productName = "") {
  const { blob, url } = await buildBrandedCard(imageUrl, productName);
  const file = new File([blob], "tashora-look.png", { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "My Tashora look", text: `My look in the ${productName || "saree"} — styled with Tashora AI ✨` });
      return;
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
  }
  triggerDownload(url);
  toast.success("Card downloaded to share");
}

export const nativeShareSupported = () =>
  typeof navigator !== "undefined" && !!navigator.canShare;
